import { Ringtone } from '@weave/schema-gen-ts/dist/shared/phonedata/v1/tenant.pb';
import { RingtoneOption } from './ringtone-dropdown';

/**
 * Filters and sorts a list of ringtone options for a call queue or call group context based on the device makes
 * present in the location. A call queue/call group can contain devices of multiple makes/models:
 *
 * Rules:
 * - RINGTONE_SILENT is always included as a universal "no ringtone" option.
 * - Yealink: only generic ringtones (models=[]) are included when the location has at least one Yealink device.
 *   Model-specific Yealink ringtones are excluded because the group/queue may ring multiple Yealink models.
 * - Polycom: all Polycom ringtones are included when the location has a Polycom device.
 * - All other makes are filtered out.
 *
 * Silent ringtones sort first; others retain their original order.
 */
export const filterRingtonesForDevices = (
  ringtones: RingtoneOption[],
  { hasYealinkDevice, hasPolycomDevice }: { hasYealinkDevice: boolean; hasPolycomDevice: boolean }
): RingtoneOption[] => {
  const sortSilentFirst = (a: RingtoneOption, b: RingtoneOption) => {
    if (a.ringtoneType === Ringtone.RINGTONE_SILENT) return -1;
    if (b.ringtoneType === Ringtone.RINGTONE_SILENT) return 1;
    return 0;
  };

  return ringtones
    .filter((ringtone) => {
      if (ringtone.ringtoneType === Ringtone.RINGTONE_SILENT) return true;
      const make = (ringtone.make ?? '').toLowerCase();
      if (make.includes('yealink')) {
        return hasYealinkDevice && (ringtone.models?.length ?? 0) === 0;
      }
      if (make.includes('polycom')) {
        return hasPolycomDevice;
      }
      return false;
    })
    .sort(sortSilentFirst);
};

/**
 * Returns the consolidated display name (e.g. "Piano Melody / Beeble") for a selected ringtone type.
 * When multiple ringtone options share the same ringtoneType (e.g. across device makes),
 * their display names are joined with " / " to match what the user sees in the dropdown.
 */
export const getConsolidatedDisplayName = ({
  filteredRingtones,
  selectedRingtone,
  selectedRingtoneType,
}: {
  filteredRingtones: RingtoneOption[];
  selectedRingtone: RingtoneOption | undefined;
  selectedRingtoneType: string | undefined;
}): string => {
  const matchingItems = filteredRingtones.filter((r) => r.ringtoneType === selectedRingtoneType);
  if (matchingItems.length <= 1) return selectedRingtone?.displayName ?? '';
  const uniqueNames = [...new Set(matchingItems.map((i) => i.displayName).filter(Boolean))];
  return uniqueNames.join(' / ');
};
